import { FC, ReactNode } from 'react';
import { FieldError, FieldErrors } from 'react-hook-form';
import { Box } from 'enova-frontend-components';
import { ErrorSummary } from '@digdir/designsystemet-react';
import { t } from 'i18next';

import { AboutFormData } from './types';

interface ErrorMessageProps {
  errors: FieldErrors<AboutFormData>;
}

const renderErrorMessages = (errors: FieldErrors, path: string[] = []): ReactNode[] => {
  // Iterate over each key-value pair in the errors object
  return Object.entries(errors).flatMap(([key, value]) => {
    // Build the path to the current field (e.g., ['user', 'name'])
    const currentPath = [...path, key];

    // If the current value has a 'message', render it as a list item
    if (value?.message && typeof value.message === 'string') {
      return (
        <ErrorSummary.Item key={currentPath.toString()} aria-label={value.message}>
          {value.message}
        </ErrorSummary.Item>
      );
    }

    // If the value is an object, recurse deeper to find nested messages
    if (typeof value === 'object') {
      return renderErrorMessages(value as FieldErrors, currentPath);
    }

    return [];
  });
};

export const Errors: FC<ErrorMessageProps> = ({ errors }) => {
  if (Object.keys(errors).length === 0) {
    return null;
  }

  return (
    <Box my={3}>
      <ErrorSummary>
        <ErrorSummary.Heading>{t('form.errors.heading')}</ErrorSummary.Heading>
        <ErrorSummary.List>
          {renderErrorMessages(errors)}
        </ErrorSummary.List>
      </ErrorSummary>
    </Box>
  );
};

export const ErrorMessage = ({ error }: { error: FieldError }) => (
  <Box color="#ac1003" pt={2} pb={3}>
    {error.message}
  </Box>
);
